import { AnyAction } from '@reduxjs/toolkit';
import { format, isSameMonth, parseISO, subMonths } from 'date-fns';
import { setFilterCompare, setFilterDate, setFilterDateCompare, setFilterTimestep } from 'src/context/reducers/filter-settings/filterSlice';
import { getDateRangeIso } from 'src/utils/format-time';
import { compareRange, defaultMaxDate, last6Months, MonthRangeApplied, MonthRangeValue, monthBounds } from 'src/utils/month-range';

export const NON_BRAND_SEARCH_PERF_PATH = '/non-brand/search-performance';

/** Picker value rebuilt from the Redux filters; compareMode isn't stored, so it is derived from the bounds. */
export function toMonthRangeValue(filters: { start: string; end: string; compare: boolean; compareStart?: string | null }): MonthRangeValue {
  const { start, end } = monthBounds(new Date(filters.start), new Date(filters.end), new Date(filters.end));
  const isLastYear = !!filters.compareStart && isSameMonth(parseISO(filters.compareStart), subMonths(start, 12));
  return { start, end, compare: filters.compare, compareMode: isLastYear ? 'last_year' : 'previous_period' };
}

/** Filter actions for an applied month range (dates serialized per the lot 1 contract). */
export function monthRangeActions(applied: MonthRangeApplied, timeZone: string): AnyAction[] {
  const { start, end } = getDateRangeIso(applied.start, applied.end, timeZone);
  const actions: AnyAction[] = [
    setFilterTimestep({ timestep: 'year_month', skipAlignment: true }),
    setFilterDate({ start, end, skipAlignment: true }),
    setFilterCompare(applied.compare),
  ];
  if (applied.compare && applied.compareStart && applied.compareEnd) {
    actions.push(setFilterDateCompare({
      compareStart: format(applied.compareStart, 'yyyy-MM-dd'),
      compareEnd: format(applied.compareEnd, 'yyyy-MM-dd'),
      skipAlignment: true,
    }));
  }
  return actions;
}

/** Page default: Last 6 Months preset (ticket 86ca7ew6c), compared with the 6 months before. */
export function defaultNonBrandMonthRange(maxDate: Date = defaultMaxDate()): MonthRangeApplied {
  const range = last6Months(maxDate);
  const value: MonthRangeValue = { ...range, compare: true, compareMode: 'previous_period' };
  const compare = compareRange(value);
  return { ...value, compareStart: compare?.start ?? null, compareEnd: compare?.end ?? null };
}
